import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Star, Globe, Calendar, User, ExternalLink, Heart, AlertCircle, 
  Loader2, ZoomIn, GitCompareArrows, Check, Play, Video, 
  EyeOff, ShieldAlert, Share2, ChevronLeft, Tag
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { RatingDialog } from "@/components/systems/RatingDialog";
import { format, isValid } from "date-fns";
import { toast } from "sonner";
import { sanitizeUrl, getYouTubeInfo } from "@/lib/utils";
import { useCompare } from "@/contexts/CompareContext";
import { QnASection } from "@/components/systems/QnASection";
import { ReviewsSection } from "@/components/systems/ReviewsSection";
import { ImageLightbox } from "@/components/systems/ImageLightbox";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";
import { SEO } from "@/components/SEO";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const REVIEWS_PER_PAGE = 5;

const SystemDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const [session, setSession] = useState<any>(null);
  const viewTracked = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
  
  const { add: addToCompare, remove: removeFromCompare, has: hasInCompare, isFull: compareFull } = useCompare();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data: viewerProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['viewer-profile', session?.user?.id],
    queryFn: async (): Promise<any> => {
      if (!session?.user?.id) return null;
      const { data } = await supabase.from('profiles').select('role').eq('id', session.user.id).single();
      return data;
    },
    enabled: !!session?.user?.id
  });

  const { data: system, isLoading: systemLoading, error } = useQuery({
    queryKey: ['system', slug],
    queryFn: async (): Promise<any> => {
      if (!slug) throw new Error("No slug");
      // FIX: Added the missing section to correctly match a full UUID format
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
      
      let query = supabase.from('systems').select(`*, category:categories(name_he), system_images(public_url, is_cover)`);
      
      if (isUuid) query = query.eq('id', slug); else query = query.eq('slug', slug);
      const { data, error } = await query.single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
    retry: 1
  });

  const sys = system as any;
  const systemId = sys?.id;

  const { data: ownerProfile } = useQuery({
    queryKey: ['system-owner', sys?.owner_id],
    queryFn: async (): Promise<any> => {
      if (!sys?.owner_id) return null;
      const { data, error } = await supabase.from('public_profiles' as any).select('id, display_name, created_at').eq('id', sys.owner_id).single();
      if (error) {
        console.error("Error fetching owner:", error);
        return null;
      }
      return data;
    },
    enabled: !!sys?.owner_id
  });

  const {
    data: reviewsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: reviewsLoading,
  } = useInfiniteQuery({
    queryKey: ['system-reviews', systemId],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, error, count } = await supabase
        .from('ratings')
        .select('id, stars, comment, created_at, user:user_profile(display_name, is_anonymous_reviews)', { count: 'exact' })
        .eq('system_id', systemId!)
        .order('created_at', { ascending: false })
        .range(pageParam, pageParam + REVIEWS_PER_PAGE - 1);
      if (error) throw error;
      return {
        items: data || [],
        totalCount: count || 0,
        nextCursor: (data?.length || 0) === REVIEWS_PER_PAGE ? pageParam + REVIEWS_PER_PAGE : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!systemId,
  });

  const allReviews = reviewsData?.pages.flatMap(p => p.items) || [];
  const totalReviews = reviewsData?.pages[0]?.totalCount || 0;
  const avgRating = sys?.rating_avg || 0;
  const ratingCount = sys?.rating_count || totalReviews;

  useEffect(() => {
    if (!systemId || viewTracked.current) return;
    const lastViewKey = `viewed_${systemId}`;
    if (sessionStorage.getItem(lastViewKey)) { viewTracked.current = true; return; }
    
    const trackView = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      let anonId = localStorage.getItem('anon_session_id') || crypto.randomUUID();
      localStorage.setItem('anon_session_id', anonId);
      
      await supabase.from('system_views' as any).insert({ 
        system_id: systemId, 
        viewer_user_id: user?.id || null, 
        anon_session_id: anonId 
      } as any);
      
      sessionStorage.setItem(lastViewKey, Date.now().toString());
      viewTracked.current = true;
    };
    trackView();
  }, [systemId]);

  const { data: userRating } = useQuery({
    queryKey: ['user-rating', systemId, session?.user?.id],
    queryFn: async (): Promise<any> => {
      if (!systemId || !session?.user?.id) return null;
      const { data } = await supabase.from('ratings').select('*').eq('system_id', systemId).eq('user_id', session.user.id).maybeSingle();
      return data;
    },
    enabled: !!systemId && !!session?.user?.id
  });

  const { data: isFavorite, refetch: refetchFavorite } = useQuery({
    queryKey: ['is-favorite', systemId, session?.user?.id],
    queryFn: async () => {
      if (!systemId || !session?.user?.id) return false;
      const { count } = await supabase.from('favorites').select('*', { count: 'exact', head: true }).eq('system_id', systemId).eq('user_id', session.user.id);
      return !!count;
    },
    enabled: !!systemId && !!session?.user?.id
  });

  const toggleFavorite = async () => {
    if (!session) { 
      toast.info("יש להתחבר כדי לשמור במועדפים");
      navigate("/login", { state: { from: location } });
      return; 
    }
    const q = isFavorite 
      ? supabase.from('favorites' as any).delete().eq('system_id', systemId).eq('user_id', session.user.id)
      : supabase.from('favorites' as any).insert({ system_id: systemId, user_id: session.user.id } as any);
    const { error } = await q;
    if (!error) { 
      toast.success(isFavorite ? "הוסר מהמועדפים" : "נוסף למועדפים"); 
      refetchFavorite();
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: sys?.title,
        text: sys?.short_desc,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("הקישור הועתק ללוח");
    }
  };

  const toggleCompare = () => {
    if (hasInCompare(sys.id)) {
      removeFromCompare(sys.id);
    } else {
      addToCompare({
        id: sys.id,
        title: sys.title,
        slug: sys.slug,
        short_desc: sys.short_desc,
        cover_image: coverImage,
        category_name: sys.category?.name_he || null,
        rating_avg: Number(avgRating) || 0,
        rating_count: ratingCount
      });
    }
  };

  const coverImage = sys?.system_images?.find((img: any) => img.is_cover)?.public_url || sys?.system_images?.[0]?.public_url || '/placeholder.svg';
  const otherImages = sys?.system_images?.filter((img: any) => img.public_url !== coverImage).map((img: any) => img.public_url) || [];
  const allImages = sys ? [coverImage, ...otherImages] : [];

  const getAbsoluteImageUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (typeof window !== "undefined") {
      return `${window.location.origin}${url}`;
    }
    return url;
  };

  const seoImage = getAbsoluteImageUrl(coverImage);
  const getHostname = (url: string) => { try { return new URL(sanitizeUrl(url) || "").hostname; } catch { return url; } };
  const safeFormat = (dateStr: string | null | undefined, formatStr: string) => { if (!dateStr) return ''; const date = new Date(dateStr); return isValid(date) ? format(date, formatStr) : ''; };
  const ensureAbsoluteUrl = (url: string | null) => sanitizeUrl(url) || '';

  const isLoading = systemLoading || (session?.user?.id && profileLoading);

  if (isLoading) return <Layout><div className="min-h-[60vh] flex flex-col items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-300 mb-4" /></div></Layout>;

  if (error || !sys) return <Layout><div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4"><AlertCircle className="w-12 h-12 text-slate-300 mb-4" /><h1 className="text-2xl font-bold text-slate-900 mb-2">שגיאה בטעינת המערכת</h1><p className="text-slate-500 max-w-md mb-6">לא הצלחנו למצוא את המערכת המבוקשת.</p><Button asChild variant="outline" className="rounded-full"><Link to="/systems">חזרה לקטלוג</Link></Button></div></Layout>;

  const isApproved = sys.status === 'approved';
  const isAdmin = (viewerProfile as any)?.role === 'admin' || (viewerProfile as any)?.role === 'super_admin';

  if (!isApproved && !isAdmin) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" dir="rtl">
          <div className="w-16 h-16 bg-slate-100 text-slate-500 rounded-full flex items-center justify-center mb-6">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">אין גישה למערכת זו</h1>
          <p className="text-slate-500 max-w-md mb-8 leading-relaxed">
            המערכת עדיין נמצאת בתהליך בדיקה ואישור, ולכן אינה זמינה לצפייה ציבורית כרגע.
          </p>
          <Button asChild variant="outline" className="rounded-full border-slate-200">
            <Link to="/systems">חזרה לקטלוג</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const { embedUrl: youtubeEmbed, isValid: hasYoutube } = getYouTubeInfo(sys.youtube_url);

  // Reusable Sidebar Content (Rendered on Desktop's right col and Mobile's top)
  const renderSidebarContent = (isMobile: boolean) => (
    <div className="space-y-6">
      {/* Main Action Card - Fiverr Style Dark */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-800">
        {!isMobile && (
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-2xl font-bold text-white leading-tight">{sys.title}</h3>
          </div>
        )}
        
        <p className="text-slate-300 text-sm leading-relaxed mb-6 font-medium">
          {sys.short_desc}
        </p>

        {/* Quick Meta List */}
        <ul className="space-y-3 mb-8 text-sm font-medium border-b border-slate-800 pb-6">
          <li className="flex items-center justify-between text-slate-300">
            <span className="flex items-center gap-2"><Globe className="w-4 h-4 text-slate-500"/> אתר</span>
            {sys.demo_url ? (
              <span className="text-white truncate max-w-[160px]" dir="ltr">
                {getHostname(sys.demo_url)}
              </span>
            ) : (
              <span className="text-slate-500">—</span>
            )}
          </li>
          <li className="flex items-center justify-between text-slate-300">
            <span className="flex items-center gap-2"><Tag className="w-4 h-4 text-slate-500"/> קטגוריה</span>
            <span className="text-white">{sys.category?.name_he}</span>
          </li>
          {isApproved && (
            <li className="flex items-center justify-between text-slate-300">
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-500"/> פורסם</span>
              <span className="text-white">{safeFormat(sys.created_at, 'dd/MM/yyyy')}</span>
            </li>
          )}
        </ul>

        {/* Primary CTA */}
        {sys.demo_url ? (
          <Button 
            asChild 
            size="lg"
            className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg rounded-xl mb-4 shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all hover:scale-[1.02]"
          >
            <a href={ensureAbsoluteUrl(sys.demo_url)} target="_blank" rel="noopener noreferrer">
              מעבר לאתר המערכת
              <ExternalLink className="w-5 h-5 ms-2 opacity-90" />
            </a>
          </Button>
        ) : (
          <div className="w-full h-14 bg-slate-800 rounded-xl flex items-center justify-center text-slate-400 font-medium mb-4">
            אין קישור זמין
          </div>
        )}

        {/* Secondary Actions Row */}
        <div className="grid grid-cols-3 gap-2">
          <button 
            onClick={toggleFavorite}
            className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          >
            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
            <span className="text-xs font-medium">{isFavorite ? 'נשמר' : 'שמור'}</span>
          </button>
          
          <button 
            onClick={toggleCompare}
            disabled={compareFull && !hasInCompare(sys.id)}
            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-colors ${hasInCompare(sys.id) ? 'text-blue-400 bg-slate-800' : compareFull ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            {hasInCompare(sys.id) ? <Check className="w-5 h-5" /> : <GitCompareArrows className="w-5 h-5" />}
            <span className="text-xs font-medium">השוואה</span>
          </button>
          
          <button 
            onClick={handleShare}
            className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          >
            <Share2 className="w-5 h-5" />
            <span className="text-xs font-medium">שתף</span>
          </button>
        </div>
      </div>
      
      {/* About the Publisher Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2">
          על המפרסם
        </h3>
        <div className="flex items-center gap-4 mb-5">
          <Avatar className="w-14 h-14 border border-slate-100 shadow-sm">
            <AvatarFallback className="bg-slate-900 text-white font-bold text-lg">
              {(ownerProfile as any)?.display_name?.[0] || <User className="w-6 h-6"/>}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-bold text-slate-900 text-lg">{(ownerProfile as any)?.display_name || "משתמש"}</div>
            <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
              חבר בקהילה מאז {safeFormat((ownerProfile as any)?.created_at, 'yyyy')}
            </div>
          </div>
        </div>
        <Button variant="outline" className="w-full font-bold h-11 border-slate-200 text-slate-700 hover:bg-slate-50" asChild>
          <Link to={`/publisher/${sys.owner_id}`}>
            צפה בפרופיל המלא
          </Link>
        </Button>
      </div>
    </div>
  );

  return (
    <Layout>
      <SEO 
        title={`${sys.title} | פרטים וביקורות`}
        description={sys.short_desc}
        image={seoImage}
        type="product"
        url={window.location.href}
      />

      <div className="bg-slate-50 min-h-screen pb-28 md:pb-20 font-sans pt-16 md:pt-24" dir="rtl">
        
        {!isApproved && (
          <div className="bg-amber-100 text-amber-900 px-4 py-3 text-center sticky top-16 z-40 flex items-center justify-center gap-2 text-sm font-medium shadow-sm">
            <EyeOff className="w-4 h-4" />
            מצב תצוגה מקדימה (מנהל): מערכת זו בסטטוס {sys.status === 'pending' ? 'ממתין לאישור' : 'נדחה/טיוטה'}.
          </div>
        )}

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          
          {/* Breadcrumbs */}
          <nav className="text-sm text-slate-500 mb-6 flex items-center gap-2 font-medium overflow-x-auto whitespace-nowrap pb-2">
            <Link to="/" className="hover:text-blue-600 transition-colors">ראשי</Link>
            <ChevronLeft className="w-4 h-4 text-slate-300" />
            <Link to="/systems" className="hover:text-blue-600 transition-colors">מערכות</Link>
            <ChevronLeft className="w-4 h-4 text-slate-300" />
            <Link to={`/systems?category=${sys.category_id}`} className="hover:text-blue-600 transition-colors">{sys.category?.name_he}</Link>
            <ChevronLeft className="w-4 h-4 text-slate-300" />
            <span className="text-slate-900 font-bold">{sys.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            
            {/* Main Content (Right Col in RTL) */}
            <div className="lg:col-span-2 space-y-10">
              
              {/* Title & Publisher Info */}
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-6 leading-tight tracking-tight">
                  {sys.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-sm pb-6 border-b border-slate-200">
                  <div className="flex items-center gap-2.5">
                    <Avatar className="w-9 h-9 border border-slate-200 shadow-sm">
                      <AvatarFallback className="bg-slate-900 text-white font-bold text-xs">
                        {(ownerProfile as any)?.display_name?.[0] || <User className="w-4 h-4"/>}
                      </AvatarFallback>
                    </Avatar>
                    <Link to={`/publisher/${sys.owner_id}`} className="font-bold text-slate-900 hover:text-blue-600 transition-colors">
                      {(ownerProfile as any)?.display_name || "משתמש"}
                    </Link>
                  </div>
                  
                  <div className="hidden sm:block w-px h-5 bg-slate-200" />
                  
                  <div className="flex items-center gap-1.5">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.round(Number(avgRating)) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                    <span className="font-bold text-amber-500 text-base">{Number(avgRating).toFixed(1)}</span>
                    <span className="text-slate-500 underline decoration-slate-300 underline-offset-4">({ratingCount} ביקורות)</span>
                  </div>
                </div>
              </div>

              {/* Gallery Carousel */}
              <div className="bg-white p-1 md:p-2 rounded-2xl border border-slate-200 shadow-sm">
                <Carousel opts={{ align: "start", direction: "rtl", loop: true }} className="w-full">
                  <CarouselContent>
                    {allImages.map((imgUrl, idx) => (
                      <CarouselItem key={idx}>
                        <div 
                          className="aspect-[16/10] md:aspect-[16/9] rounded-xl overflow-hidden cursor-zoom-in group relative bg-slate-100"
                          onClick={() => setLightboxIndex(idx)}
                        >
                          <img src={imgUrl} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {allImages.length > 1 && (
                    <div className="hidden sm:block">
                      <CarouselPrevious className="right-4 left-auto bg-white/90 hover:bg-white border-none shadow-md w-10 h-10" />
                      <CarouselNext className="left-4 right-auto bg-white/90 hover:bg-white border-none shadow-md w-10 h-10" />
                    </div>
                  )}
                </Carousel>
              </div>

              {/* MOBILE SIDEBAR (Appears right after gallery on mobile) */}
              <div className="block lg:hidden">
                {renderSidebarContent(true)}
              </div>

              {/* Full Description (About) */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">אודות המערכת</h2>
                <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
                  <MarkdownRenderer content={sys.full_desc} allowStyle={true} />
                </div>
              </div>

              {/* Video Section */}
              {(hasYoutube && youtubeEmbed) && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <Play className="w-6 h-6 text-red-600 fill-red-600" />
                    סרטון הדגמה
                  </h2>
                  <div className="aspect-video w-full rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-black">
                    <iframe src={youtubeEmbed} className="w-full h-full border-none" allowFullScreen />
                  </div>
                </div>
              )}
              
              {!hasYoutube && sys.youtube_url && (
                <div className="bg-slate-900 p-6 rounded-2xl shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                   <div className="flex items-center gap-4 text-white">
                     <div className="p-3 bg-white/10 rounded-xl"><Video className="w-6 h-6" /></div>
                     <div>
                       <h4 className="font-bold text-lg">סרטון וידאו חיצוני</h4>
                       <p className="text-sm text-slate-400">צפה בהדגמה מקיפה ב-YouTube</p>
                     </div>
                   </div>
                   <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg border-none" asChild>
                     <a href={ensureAbsoluteUrl(sys.youtube_url)} target="_blank" rel="noopener noreferrer">
                       פתח וידאו
                       <ExternalLink className="w-4 h-4 ms-2 opacity-80" />
                     </a>
                   </Button>
                </div>
              )}

              {/* Reviews Stacked */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm" id="reviews">
                <ReviewsSection 
                  systemId={sys.id}
                  systemOwnerId={sys.owner_id}
                  session={session}
                  userRating={userRating}
                  allReviews={allReviews}
                  totalReviews={totalReviews}
                  isLoading={reviewsLoading}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                />
              </div>

              {/* QnA Stacked */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm" id="qna">
                <QnASection systemId={sys.id} ownerId={sys.owner_id} />
              </div>
            </div>

            {/* Sidebar (Left Col in RTL) - Desktop Only */}
            <div className="hidden lg:block lg:col-span-1 relative">
              <div className="sticky top-24 space-y-6">
                {renderSidebarContent(false)}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 p-4 pb-safe md:hidden z-[60] flex gap-3 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
        {sys.demo_url && (
          <Button className="flex-1 rounded-xl h-14 text-base bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg" asChild>
            <a href={ensureAbsoluteUrl(sys.demo_url)} target="_blank" rel="noopener noreferrer">
              בקר באתר
              <ExternalLink className="w-5 h-5 ms-2 opacity-90" />
            </a>
          </Button>
        )}
        
        {session ? (
          sys.owner_id !== session.user.id && (
            <RatingDialog 
              systemId={sys.id} 
              existingRating={userRating} 
              trigger={
                <Button variant="outline" className="flex-1 rounded-xl h-14 text-base border-slate-200 text-slate-900 font-bold bg-white shadow-sm">
                  <Star className="w-5 h-5 ms-2 text-slate-900" />
                  {userRating ? "ערוך דירוג" : "דרג עכשיו"}
                </Button>
              } 
            />
          )
        ) : (
          <Button variant="outline" asChild className="flex-1 rounded-xl h-14 text-base border-slate-200 text-slate-900 font-bold bg-white shadow-sm">
            <Link to="/login" state={{ from: location }}>התחבר לדירוג</Link>
          </Button>
        )}
      </div>

      <ImageLightbox
        images={allImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxIndex !== -1}
        onClose={() => setLightboxIndex(-1)}
      />
    </Layout>
  );
};

export default SystemDetails;